import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Modal from '@groovehq/internal-design-system/lib/components/Modal/Modal'
import ProgressDots from '@groovehq/internal-design-system/lib/components/ProgressDots/ProgressDots'
import { releaseHold } from 'util/dirtyHolds'
import {
  useRebuildLeftNavMenu,
  useRedirectToChannel,
} from 'ducks/channels/hooks'
import { useDrawer } from 'ducks/drawers/hooks'
import { DRAWER_TYPE_CHANNELS_MEMBERS_ADD } from 'ducks/drawers/types'
import teammate from 'assets/teammate.png'
import { doClearChannelDraft } from 'ducks/channels/actions'
import { selectIsInInbox } from 'selectors/location'
import { useAdminAccess } from '../../NoAccess/hooks'
import { CHANNEL_EMAIL_TYPES } from '../Channels.data'
import { styles } from './styles'

const AddMoreMembers = ({
  drawerId,
  drawerResourceId: channelId,
  drawerChannelType: channelType = 'forwarding',
  onExit,
}) => {
  const dispatch = useDispatch()
  const { rebuildExitAndRedirect } = useRedirectToChannel({ channelId, onExit })
  const isInInbox = useSelector(selectIsInInbox)
  const { rebuildMenuAndExit } = useRebuildLeftNavMenu({ channelId, onExit })

  const { openDrawer: openAddMembers } = useDrawer({
    id: null,
    type: DRAWER_TYPE_CHANNELS_MEMBERS_ADD,
    closeIgnoresStack: false,
  })

  useAdminAccess(onExit)

  const handleOnExit = useCallback(
    () => {
      // we do not rebuild left nav as soon as channel is created anymore
      // if the user clicks the Exit button on top right, rebuild nav
      if (isInInbox) {
        rebuildExitAndRedirect()
        return
      }
      rebuildMenuAndExit()
    },
    [isInInbox, rebuildMenuAndExit, rebuildExitAndRedirect]
  )

  const handleOpenMailbox = useCallback(
    () => {
      releaseHold(drawerId)
      rebuildExitAndRedirect()
      dispatch(doClearChannelDraft(channelId))
    },
    [drawerId, channelId, dispatch, rebuildExitAndRedirect]
  )

  const handleOpenAddMembers = useCallback(
    () => {
      openAddMembers(channelId)
    },
    [channelId, openAddMembers]
  )

  return (
    <Modal
      onClose={handleOnExit}
      dataTestId="channels-members-add-more"
      portal="#drawer-root"
      open
      className="grui pb-14"
      backdropTransparency="light"
    >
      <ProgressDots
        count={CHANNEL_EMAIL_TYPES[channelType].steps}
        now={CHANNEL_EMAIL_TYPES[channelType].steps}
        className="grui my-11"
      />
      <Modal.Title>Ready to bring on the team?</Modal.Title>
      <Modal.Description className="grui px-4">
        {`Invite any ${app.t('agent')} that needs access to this ${app.t(
          'mailbox'
        )} to your Groove account.`}
      </Modal.Description>
      <div css={styles.content} className="grui pt-10 pb-6 text-center">
        <img src={teammate} alt={`Add ${app.t('agents')}`} height="294" />
        <Modal.Button
          type="info"
          onClick={handleOpenAddMembers}
          className="grui mt-10"
          data-test-id="invite-agents-button"
        >
          {`Invite ${app.t('agents')}!`}
        </Modal.Button>
        <Modal.Button
          type="tertiary"
          onClick={handleOpenMailbox}
          className="grui mt-5"
          data-test-id="finish-and-redirect-inbox"
        >
          No thanks! {`Take me to my ${app.t('mailbox')}.`}
        </Modal.Button>
      </div>
    </Modal>
  )
}

export default AddMoreMembers
